import { useEffect, useRef, useState } from 'react';

interface Props {
    fileSrc: string;
    size: number;
    columns: number;
    totalTiles: number;
    duration: number;
}

const AnimatedSpritesheet: React.FC<Props> = ({ fileSrc, size, columns, totalTiles, duration }) => {
    const root = useRef<HTMLDivElement | null>(null);
    const animationFrameId = useRef<number | null>(null);
    const startTime = useRef<number | null>(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        const animate = (timestamp: number) => {
            if (!root.current) {
                return;
            }

            if (!startTime.current) {
                startTime.current = timestamp;
            }

            const elapsed = timestamp - startTime.current;

            const progress = (elapsed % duration) / duration;
            const currentFrame = Math.floor(progress * totalTiles);

            const row = Math.floor(currentFrame / columns);
            const column = currentFrame % columns;

            const x = column * size;
            const y = row * size;

            setPosition({ x, y });

            animationFrameId.current = requestAnimationFrame(animate);
        };

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                animationFrameId.current = requestAnimationFrame(animate);
            } else if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
                animationFrameId.current = null;
                startTime.current = null;
            }
        });

        if (root.current) {
            observer.observe(root.current);
        }

        return () => {
            observer.disconnect();
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, []);

    return (
        <div
            style={{
                width: size,
                height: size,
                backgroundImage: `url(${fileSrc})`,
                backgroundSize: `${size * columns}px auto`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: `-${position.x}px -${position.y}px`
            }}
            ref={root}
        />
    );
};

export default AnimatedSpritesheet;
